// ============= AUTH SERVICE TYPES =============

export interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  hasSpecial: boolean;
}

export interface PasswordApiResponse {
  success: boolean;
  hashedPassword?: string;
  message: string;
}

export interface AuthApiResponse {
  success: boolean;
  message: string;
}

export interface UserRegistrationResult {
  success: boolean;
  message: string;
  keyPair?: {
    publicKey: Uint8Array;
    privateKey: Uint8Array;
  };
}

export interface UserLoginResult {
  success: boolean;
  message: string;
  derivedKey?: string;
}

// ============= POC AUTH SERVICE INTERFACE =============

/**
 * Authentication service focused on business logic and API interactions.
 * Delegates all cryptographic operations to CryptoService via dependency injection.
 */
export abstract class PocAuthService {

  // ============= PASSWORD GENERATION =============

  /**
   * Generate a cryptographically secure random password
   * @param length Password length (default: 12)
   * @returns Generated password string
   */
  abstract generateSecurePassword(length?: number): string;

  /**
   * Generate a deterministic password from email (for demos/testing)
   * @param email Email address to use as seed
   * @param length Password length (default: 12)
   * @returns Deterministic password string
   */
  abstract generateDeterministicPassword(email: string, length?: number): string;

  // ============= PASSWORD VALIDATION =============

  /**
   * Validate password against security requirements
   * @param password The password to validate
   * @returns Validation results for each requirement
   */
  abstract validatePassword(password: string): PasswordValidation;

  /**
   * Check if password meets all validation requirements
   * @param validations The validation results to check
   * @returns True if all validations pass
   */
  abstract isPasswordValid(validations: PasswordValidation): boolean;

  // ============= API OPERATIONS =============

  /**
   * Fetch stored password hash from API
   * @param email User's email address
   * @returns API response with password hash if successful
   */
  abstract fetchPassword(email: string): Promise<PasswordApiResponse>;

  /**
   * Save password to API (hashes password using crypto service)
   * @param email User's email address
   * @param password User's plaintext password
   * @returns API response indicating success/failure
   */
  abstract savePassword(email: string, password: string): Promise<AuthApiResponse>;

  /**
   * Store user's public/private keys via API
   * @param email User's email address
   * @param publicKey User's ML-KEM public key
   * @param privateKeyHash Hash of user's private key
   * @returns API response indicating success/failure
   */
  abstract sendKeys(
    email: string,
    publicKey: Uint8Array,
    privateKeyHash: string
  ): Promise<AuthApiResponse>;

}
